var farmingTypeActions = require('@actions/farmingTypeActions');
const dayjs = require('../../utils/dayjsConfig');
var axios = require('axios');
var { error_message } = require('@managers/helpers');
var distributionActions = require('@actions/distributionActions');
var fertilizerDataActions = require('@actions/fertilizerDataActions');
var remoteConfigurationActions = require('@actions/remoteConfigurationActions');
var notificationsManager = require('@managers/notificationsManager');
var { getMixpanelInstance } = require('@managers/mixpanelManager');

const SCIROOT_USER_PATH = 'user';
const SCIROOT_USERDATA_PATH = 'userdata';
const SCIROOT_USERDATA_CONFIGURATION_PATH = 'userdata_field_configuration';

const units_details = {
  metric_india: {
    area: 'Acre',
    length: 'mm',
    pressure: 'CBar',
    temperature: 'Celsius',
    volume: 'Cubic Metre',
    weight: 'Kg',
  },
  metric_israel: {
    area: 'Dunam',
    length: 'mm',
    pressure: 'CBar',
    temperature: 'Celsius',
    volume: 'Cubic Metre',
    weight: 'Kg',
  },
  metric_australia: {
    area: 'Hectare',
    length: 'mm',
    pressure: 'CBar',
    temperature: 'Celsius',
    volume: 'Megaliter',
    weight: 'Kg',
  },
  metric: {
    area: 'Hectare',
    length: 'mm',
    pressure: 'CBar',
    temperature: 'Celsius',
    volume: 'Cubic Metre',
    weight: 'Kg',
  },
  imperial: {
    area: 'Acre',
    length: 'Inch',
    pressure: 'CBar',
    temperature: 'Fahrenheit',
    volume: 'Acre Foot',
    weight: 'lb',
  },
};

const dispatchUnitsChangeReload = async (dispatch, getState) => {
  // Units are baked into the tree server-side, so the cached / in-memory trees
  // are only valid for the unit system they were fetched with. Drop both before
  // reloading — load_distributions(true) only refetches the selected tree, so
  // without this the other distributions keep old-unit values (and get
  // re-persisted to IndexedDB in mixed units). The await ensures the delete
  // completes before the fresh load's persist can run.
  await dispatch(distributionActions.clear_distributions_cache());
  // Await the reload: clear_distributions_cache nulled the in-memory tree, so
  // the selected tree must be rebuilt before the history / recommendation
  // thunks below run — they read distribution_data.distributions and would
  // otherwise hit a null map (no refetch, and get_farming_units_recommendations
  // used to throw). load_distributions refetches only the selected tree; the
  // other distributions come back as skeleton and reload on demand in new units.
  await dispatch(distributionActions.load_distributions(true));
  // Re-fetch selected farming-unit / field history in the new units
  // (ir/accu_etc are server-shaped).
  const { selected_entities } = getState().distribution_data || {};
  const distId = selected_entities?.distribution?.id;
  const fuId = selected_entities?.farming_unit?.id;
  const fieldId = selected_entities?.field?.id;

  if (distId && fuId) {
    dispatch(distributionActions.get_farming_units_history(distId, fuId, null));
    dispatch(
      distributionActions.get_farming_units_recommendations(distId, fuId)
    );
    if (fieldId) {
      dispatch(distributionActions.get_field_history(distId, fuId, fieldId));
    }
  }
  dispatch(fertilizerDataActions.load_fertilizer_data(true));
  dispatch(farmingTypeActions.load_farming_type_data(true));
};

export var get_user_data = userData => {
  return (dispatch, getState) => {
    dispatch(initiate_set_user_attributes());

    axios
      .get(SCIROOT_USERDATA_PATH, userData)
      .then(function (res) {
        var user_details = {
          ...res.data.units,
          ...units_details[res.data.units.unit_system],
          ...res.data.settings,
          distributions: { ...res.data.distributions },
          meta_data: { ...res.data.meta_data },
          created_at: res.data.created_at,
        };

        // Sending User Creation date to Mixpanel
        let mixpanelInstance = getMixpanelInstance();

        if (user_details.created_at && mixpanelInstance) {
          let createdAtDate = dayjs.unix(parseInt(user_details.created_at));
          let formattedCreatedAtTime = createdAtDate.format(
            'YYYY-MM-DD HH:mm:ss'
          );

          mixpanelInstance.getPeople().set({
            'User Creation Date': formattedCreatedAtTime,
          });
        }

        dispatch(get_user_data_success(user_details));
        dispatch(remoteConfigurationActions.get_remote_config());
      })
      .catch(function (err) {
        console.log(error_message(err));
        dispatch(set_user_attributes_fail(error_message(err)));
      });
  };
};

// It's a new version of set user attributes, and will probably remove the set_user_attributes in the future
export var set_user_data = userData => {
  return (dispatch, getState) => {
    dispatch(initiate_set_user_attributes());

    axios
      .put(SCIROOT_USERDATA_PATH, { dataDictionary: userData })
      .then(function (res) {
        var user_details = {
          ...res.data.units,
          ...units_details[res.data.units.unit_system],
          ...res.data.settings,
          distributions: { ...res.data.distributions },
          meta_data: { ...res.data.meta_data },
          created_at: res.data.created_at,
        };
        dispatch(get_user_data_success(user_details));
        dispatch(remoteConfigurationActions.get_remote_config());
        dispatchUnitsChangeReload(dispatch, getState);
      })
      .catch(function (err) {
        console.log(error_message(err));
        dispatch(set_user_attributes_fail(error_message(err)));
      });
  };
};

/**
 * Update user settings in /userdata without triggering heavy reloads.
 * Use this for small, frequent per-user flags (e.g. coachmarks shown once).
 */
export var update_user_settings = settings => {
  return (dispatch, getState) => {
    dispatch(initiate_set_user_attributes());

    axios
      .put(SCIROOT_USERDATA_PATH, { dataDictionary: { settings: settings } })
      .then(function (res) {
        var user_details = {
          ...res.data.units,
          ...units_details[res.data.units.unit_system],
          ...res.data.settings,
          distributions: { ...res.data.distributions },
          meta_data: { ...res.data.meta_data },
          created_at: res.data.created_at,
        };
        dispatch(get_user_data_success(user_details));
      })
      .catch(function (err) {
        console.log(error_message(err));
        dispatch(set_user_attributes_fail(error_message(err)));
      });
  };
};

/**
 * Mark the All Farm tab coachmark ("new_section") as "seen" (once per user).
 * Persisted via /userdata.
 */
export var set_new_section_seen = function () {
  return function (dispatch, getState) {
    dispatch(initiate_set_user_attributes());

    // Backend supports updating `settings` as a whole. To avoid overwriting existing
    // settings keys, we first fetch current user data, merge our onboarding flag,
    // then PUT the full settings object.
    axios
      .get(SCIROOT_USERDATA_PATH)
      .then(function (res) {
        if (!res || !res.data) {
          throw new Error('Invalid response from server');
        }
        var currentSettings = res.data.settings || {};
        var onboarding = currentSettings.onboarding || {};
        onboarding.new_section_seen = true;
        currentSettings.onboarding = onboarding;

        return axios.put(SCIROOT_USERDATA_PATH, {
          dataDictionary: { settings: currentSettings },
        });
      })
      .then(function (res2) {
        if (!res2 || !res2.data || !res2.data.units || !res2.data.settings) {
          throw new Error('Invalid response from PUT request');
        }
        var user_details = {
          ...res2.data.units,
          ...units_details[res2.data.units.unit_system],
          ...res2.data.settings,
          distributions: { ...res2.data.distributions },
          meta_data: { ...res2.data.meta_data },
          created_at: res2.data.created_at,
        };
        dispatch(get_user_data_success(user_details));
      })
      .catch(function (err) {
        console.log(error_message(err));
        dispatch(set_user_attributes_fail(error_message(err)));
      });
  };
};

export var set_user_meta_data = userData => {
  return (dispatch, getState) => {
    dispatch(initiate_set_user_attributes());

    axios
      .put(SCIROOT_USERDATA_PATH, { dataDictionary: userData })
      .then(function (res) {
        var user_details = {
          ...res.data.units,
          ...units_details[res.data.units.unit_system],
          ...res.data.settings,
          distributions: { ...res.data.distributions },
          meta_data: { ...res.data.meta_data },
          created_at: res.data.created_at,
        };
        dispatch(get_user_data_success(user_details));
      })
      .catch(function (err) {
        console.log(error_message(err));
        dispatch(set_user_attributes_fail(error_message(err)));
      });
  };
};

export var set_field_configuration = (
  distribution_id,
  farming_unit_id,
  fields_ids,
  configuration_object
) => {
  return (dispatch, getState) => {
    dispatch(initiate_set_user_attributes());

    axios
      .put(SCIROOT_USERDATA_CONFIGURATION_PATH, {
        distribution_id: distribution_id,
        farming_unit_id: farming_unit_id,
        fields_ids: fields_ids,
        configuration_object: configuration_object,
      })
      .then(function (res) {
        // Reload user data to sync configurations in Redux state
        // Note: get_user_data() has its own error handling - dispatches set_user_attributes_fail on error
        dispatch(get_user_data());
      })
      .catch(function (err) {
        console.log(error_message(err));
        dispatch(set_user_attributes_fail(error_message(err)));
      });
  };
};

// Middleware
export var set_user_attributes = attributes => {
  return (dispatch, getState) => {
    dispatch(initiate_set_user_attributes());

    axios
      .put(SCIROOT_USER_PATH, attributes)
      .then(function (res) {
        var user_details = res.data.user_details;

        dispatch(set_user_attributes_success(user_details));
      })
      .catch(function (err) {
        console.log(error_message(err));
        dispatch(set_user_attributes_fail(error_message(err)));
      });
  };
};

export var get_notifications_for_user = () => {
  return (dispatch, getState) => {
    dispatch(initiate_get_user_notifications());

    notificationsManager
      .getNotificationsForUser(dayjs().unix())
      .then(res => {
        dispatch(get_user_notifications_success(res));
      })
      .catch(function (err) {
        console.log(error_message(err));
        dispatch(get_user_notifications_fail(err));
      });
  };
};

export var set_discharge_test = (field_id, discharge_test) => {
  return (dispatch, getState) => {
    dispatch(initiate_discharge_test(field_id, discharge_test));
  };
};

export var initiate_discharge_test = (field_id, discharge_test) => {
  return {
    type: 'SET_DISCHARGE_TEST',
    field_id: field_id,
    discharge_test: discharge_test,
  };
};

export var initiate_get_user_notifications = () => {
  return {
    type: 'GET_USER_NOTIFICATIONS_INIT',
  };
};

export var get_user_notifications_success = userNotifications => {
  return {
    type: 'GET_USER_NOTIFICATIONS_SUCCESS',
    user_notifications: userNotifications,
  };
};

export var get_user_notifications_fail = get_user_notifications_error => {
  return {
    type: 'GET_USER_ATTRIBUTES_FAIL',
    get_user_notifications_error,
  };
};

export var initiate_set_user_attributes = () => {
  return {
    type: 'SET_USER_ATTRIBUTES_INIT',
  };
};

export var set_user_attributes_success = user_details => {
  return {
    type: 'SET_USER_ATTRIBUTES_SUCCESS',
    user_details: user_details,
  };
};

export var set_user_attributes_fail = set_user_attributes_error => {
  return {
    type: 'SET_USER_ATTRIBUTES_FAIL',
    set_user_attributes_error,
  };
};

export var get_user_data_success = userData => {
  return {
    type: 'GET_USER_DATA_SUCCESS',
    userData: userData,
  };
};

export var delete_user_data = function (password, reason) {
  return function (dispatch, getState) {
    dispatch(initiate_delete_user_data());

    var data = { password: password };
    if (reason) {
      data.reason = reason;
    }

    return axios
      .delete(SCIROOT_USERDATA_PATH, { data: data })
      .then(function (res) {
        dispatch(delete_user_data_success());
      })
      .catch(function (err) {
        console.log(error_message(err));
        dispatch(delete_user_data_fail(error_message(err)));
        throw err;
      });
  };
};

export var initiate_delete_user_data = function () {
  return {
    type: 'DELETE_USER_DATA_INIT',
  };
};

export var delete_user_data_success = function () {
  return {
    type: 'DELETE_USER_DATA_SUCCESS',
  };
};

export var delete_user_data_fail = function (error) {
  return {
    type: 'DELETE_USER_DATA_FAIL',
    delete_user_data_error: error,
  };
};

export var set_user_settings = has_previewed => {
  return {
    type: 'HIDDEN_ITEM_HAS_PREVIEWED',
    has_previewed,
  };
};

export var start_units_recalc = (hadFieldSelected, hadFarmingUnitSelected) => {
  return {
    type: 'START_UNITS_RECALC',
    hadFieldSelected: !!hadFieldSelected,
    hadFarmingUnitSelected: !!hadFarmingUnitSelected,
  };
};

export var stop_units_recalc = () => {
  return {
    type: 'STOP_UNITS_RECALC',
  };
};
